import React from 'react';
import './Wordcloud.css';

import { WordCloudEntry, Sex } from 'lib/Types';

import * as am4core from "@amcharts/amcharts4/core";
import * as am4plugins_wordCloud from "@amcharts/amcharts4/plugins/wordCloud";
import am4lang_de_DE from "@amcharts/amcharts4/lang/de_DE";
import am4lang_it_IT from "@amcharts/amcharts4/lang/it_IT";

import info from 'data/info.json';

interface WordCloudDataEntry extends WordCloudEntry {
    color: string;
    opacity: number;
}

interface Props {
    words: WordCloudEntry[];
    nameSelected(name: string, sex?: Sex): void;
}

type MinMaxPair = [number, number];

class WordCloud extends React.Component<Props, {}> {
    private divName: string;
    private wordCloud: am4plugins_wordCloud.WordCloud | null;
    private maleBounds: MinMaxPair;
    private femaleBounds: MinMaxPair;

    constructor(props: Props) {
        super(props);

        this.maleBounds = [0, 0];
        this.femaleBounds = [0, 0];

        this.divName = `wordCloud`;
        this.wordCloud = null;
    }

    private mapOpacity(value: number, bounds: MinMaxPair): number {
        const slope = (1 - 0.6) / (bounds[1] - bounds[0]);
        return 0.6 + slope * (value - bounds[0]);
    }

    private convertData() {
        if (this.props.words.length && this.props.words[0].sex === undefined) {
            const values = this.props.words.map(item => item.value);
            this.maleBounds = [Math.min(...values), Math.max(...values)];
            this.femaleBounds = [0, 0];
        } else {
            const valuesM = this.props.words.filter(item => item.sex === Sex.Male).map(item => item.value);
            const valuesF = this.props.words.filter(item => item.sex === Sex.Female).map(item => item.value);
            this.maleBounds = [Math.min(...valuesM), Math.max(...valuesM)];
            this.femaleBounds = [Math.min(...valuesF), Math.max(...valuesF)];
        }

        const data: WordCloudDataEntry[] = [];
        for (let item of this.props.words) {
            data.push({
                text: item.text,
                value: item.value,
                sex: item.sex,
                color: item.sex ? (item.sex === Sex.Male ? info.colorMale : info.colorFemale) : info.colorLastname,
                opacity: this.mapOpacity(item.value, item.sex === Sex.Female ? this.femaleBounds : this.maleBounds)
            })
        }

        return data;
    }

    componentDidMount() {
        const chart = am4core.create(this.divName, am4plugins_wordCloud.WordCloud);
        chart.fontFamily = '"Helvetica Neue", Helvetica, Arial, sans-serif';

        const series = chart.series.push(new am4plugins_wordCloud.WordCloudSeries());
        series.interpolationDuration = 0;
        series.randomness = 0.45;
        series.accuracy = 1;
        series.angles = [0];
        series.minFontSize = 14;
        series.maxFontSize = 75;
        const chartLanguage = new am4core.Language();
        chartLanguage.locale = window.lang === "de" ? am4lang_de_DE : am4lang_it_IT;
        series.numberFormatter.language = chartLanguage;

        series.data = this.convertData();

        series.dataFields.word = "text";
        series.dataFields.value = "value";

        series.labels.template.tooltipText = "{word}: {value}";
        series.labels.template.propertyFields.fill = "color";
        series.labels.template.propertyFields.fillOpacity = "opacity";
        series.labels.template.cursorOverStyle = am4core.MouseCursorStyle.pointer;

        series.labels.template.events.on("hit", (event) => {
            const item = (event.target.dataItem?.dataContext as WordCloudDataEntry)
            this.props.nameSelected(item.text, item.sex);
        });

        this.wordCloud = chart;
    }

    componentWillUnmount() {
        if (this.wordCloud)
            this.wordCloud.dispose();
    }

    componentDidUpdate(prevProps: Props) {
        const isNewData = () => {
            if (prevProps.words !== this.props.words && prevProps.words.length !== this.props.words.length)
                return true;
            for (let item of this.props.words) {
                if (!prevProps.words.find(i => i.text === item.text))
                    return true;
            }

            return false;
        }

        if (this.wordCloud) {
            if (isNewData()) {
                this.wordCloud.series.getIndex(0)!.data = this.convertData();
                this.wordCloud.series.getIndex(0)!.invalidateRawData();
            }
        }
    }

    render() {
        return (
            <div id={this.divName} className='wordCloud-container'></div>
        );
    }
}

export default WordCloud;